import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, Phone } from "lucide-react";
import { ContactForm } from "./ContactForm";
import { COMPANY } from "../data";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { useLang } from "../i18n";

export const QuoteModal = ({ product, open, onClose }) => {
  const { t } = useLang();
  const [waOpen, setWaOpen] = useState(false);

  useEffect(() => {
    if (!open) setWaOpen(false);
  }, [open]);

  const waMessage = product ? t("lightbox.waMessage", product.name) : "";
  const waLink = (num) => `https://wa.me/${num.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(waMessage)}`;

  return (
    <AnimatePresence>
      {open && product && (
        <motion.div
          data-testid="quote-modal"
          className="fixed inset-0 z-[140] flex items-center justify-center p-4 md:p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => { e.stopPropagation(); onClose(); }}
        >
          <div className="absolute inset-0 bg-[#0D0C0B]/95 backdrop-blur-xl" />
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 w-full max-w-lg bg-[#141210] ring-1 ring-white/10 rounded-lg p-8 md:p-10 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="quote-close"
              onClick={onClose}
              aria-label="Kapat"
              className="absolute top-4 right-4 w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-[#F2EFEB] hover:bg-[#7BA05B] hover:border-[#7BA05B] hover:text-[#0D0C0B] transition-all duration-300"
            >
              <X size={16} strokeWidth={1.5} />
            </button>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-[#7BA05B]" />
              <span className="font-sans uppercase tracking-[0.25em] text-[10px] text-[#C67D57]">{t("lightbox.quote")}</span>
            </div>
            <h3 translate="no" className="notranslate font-serif text-2xl md:text-3xl text-[#F2EFEB] tracking-tight leading-tight mb-2">
              {product.name}
            </h3>
            <p className="text-[#8C8681] text-sm mb-6">{t("lightbox.quoteSub")}</p>

            {/* WhatsApp — ziyaretçi numarayı seçer */}
            <div className="mb-6">
              {!waOpen ? (
                <button
                  type="button"
                  data-testid="quote-whatsapp-toggle"
                  onClick={() => setWaOpen(true)}
                  className="w-full flex items-center justify-center gap-2.5 bg-[#25D366] text-[#0D0C0B] font-sans uppercase tracking-[0.15em] text-[11px] py-4 rounded-full hover:bg-[#1EBE5B] transition-all duration-300"
                >
                  <WhatsAppIcon size={17} />
                  {t("lightbox.waButton")}
                </button>
              ) : (
                <div data-testid="quote-whatsapp-numbers">
                  <p className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#8C8681] mb-2">{t("lightbox.waSelect")}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {COMPANY.whatsapp.map((p, i) => (
                      <a
                        key={i}
                        href={waLink(p)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`quote-whatsapp-${i}`}
                        className="flex items-center gap-2 bg-[#25D366]/10 border border-[#25D366]/40 text-[#F2EFEB] text-xs py-2.5 px-3 rounded-lg hover:bg-[#25D366]/20 transition-all duration-300"
                      >
                        <WhatsAppIcon size={14} className="text-[#25D366] shrink-0" />
                        {p}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-4 mb-6">
              <span className="h-px flex-1 bg-white/10" />
              <span className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#8C8681]">{t("lightbox.orForm")}</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>

            <ContactForm
              initialMessage={t("lightbox.quotePrefill", product.name)}
              onSubmitted={onClose}
            />

            <div data-testid="quote-contact-info" className="mt-8 pt-6 border-t border-white/10 space-y-4">
              <p className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#8C8681]">{t("lightbox.direct")}</p>
              <a
                href={`mailto:${COMPANY.email}`}
                data-testid="quote-email"
                className="flex items-center gap-3 text-[#F2EFEB] hover:text-[#C67D57] transition-colors"
              >
                <Mail size={16} strokeWidth={1.5} className="text-[#A65D37]" />
                <span className="text-sm">{COMPANY.email}</span>
              </a>
              <div className="grid grid-cols-2 gap-2">
                {COMPANY.phone.map((p, i) => (
                  <a
                    key={i}
                    href={`tel:${p.replace(/\s/g, "")}`}
                    data-testid={`quote-phone-${i}`}
                    className="flex items-center gap-2 border border-[#A65D37]/40 text-[#F2EFEB] text-xs py-2.5 px-3 rounded-lg hover:bg-[#A65D37]/15 transition-all duration-300"
                  >
                    <Phone size={14} strokeWidth={1.5} className="text-[#A65D37] shrink-0" />
                    {p}
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
